import { inject, Injectable } from '@angular/core';
import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { PartnerProgram } from '@api/models/B2bService/Contract/partner-program';
import { Observable } from 'rxjs';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { B2BService } from '@api/services/b-2-b.service';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { AddB2BProgramRequest } from '@api/models/B2bService/Contract/add-b-2-b-program-request';
import { ProgramService } from '@api/services/program.service';
import { SetB2BProgramRequest } from '@api/models/B2bService/Contract/set-b-2-b-program-request';
import { Service } from '@api/models/B2bService/Contract/service';
import { Tariffs } from '@api/models/B2bService/Contract/tariffs';
import { tap } from 'rxjs/operators';
import { B2BPartnerListResponse } from '@api/models/B2bService/Contract/B2bPartner/b-2-b-partner-list-response';
import { CustomRxState } from '@core/state/rx.state';
import { B2BPartnerService } from '@api/services/b-2-b-partner.service';
import { B2BVisOrgListResponse } from '@api/models/B2bService/Contract/B2bPartner/b-2-b-vis-org-list-response';


interface IB2bProgramsState {
    loading: Record<StartWithUnit, boolean>;
    programs: PartnerProgram[];
    services: Service[];
    tickets: Tariffs[];
    partners: B2BPartnerListResponse[];
    organizations: B2BVisOrgListResponse[];
}

interface B2bProgramsAction {
    requestPrograms: PartnerProgram[];
    requestServices: Service[];
    requestTickets: Tariffs[];
    partners: B2BPartnerListResponse[];
}

@Injectable({
    providedIn: 'root',
})
export class B2bProgramsState extends CustomRxState<IB2bProgramsState> {
    readonly #b2BService: B2BService = inject(B2BService);
    readonly #programService: ProgramService = inject(ProgramService);
    readonly #b2BPartnerService: B2BPartnerService = inject(B2BPartnerService);

    public readonly programs$: Observable<PartnerProgram[]> = this.select('programs');
    public readonly services$: Observable<Service[]> = this.select('services');
    public readonly tickets$: Observable<Tariffs[]> = this.select('tickets');
    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly partners$: Observable<B2BPartnerListResponse[]> = this.selectLazy('partners', this.requestPartners);
    public readonly organizations$: Observable<B2BVisOrgListResponse[]> = this.selectLazy('organizations', this.requestOrganizations);
    private readonly actions: RxActions<B2bProgramsAction> = new RxActionFactory<B2bProgramsAction>().create();

    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();
    }

    private setDefaultState(): void {
        this.set({
            programs: null,
            services: null,
        });
    }

    private connectSelectors(): void {
        this.connect('programs', this.actions.requestPrograms$);
        this.connect('services', this.actions.requestServices$);
        this.connect('tickets', this.actions.requestTickets$);
    }

    public requestPrograms(): void {
        this.hold(
            this.#b2BService.apiB2BListB2BProgramsGet().pipe(
                startWithLoading(this, StartWithUnit.RequestB2BPrograms),
            ),
            this.actions.requestPrograms,
        );
    }

    public requestServices(): void {
        this.hold(this.#b2BService.apiB2BListB2BServiceGet()
            .pipe(startWithLoading(this, StartWithUnit.RequestB2BServices)), this.actions.requestServices);
    }

    public createProgram(body: AddB2BProgramRequest): Observable<unknown> {
        return this.#b2BService.apiB2BAddB2BProgramPost({ body }).pipe(
            tap(() => this.requestPrograms()),
            startWithLoading(this, StartWithUnit.RequestAddB2BProgram),
        );
    }

    public updateProgram(body: SetB2BProgramRequest): Observable<unknown> {
        return this.#programService.apiB2BSetB2BProgramPost({ body }).pipe(
            tap(() => this.requestPrograms()),
            startWithLoading(this, StartWithUnit.RequestUpdateB2BProgram),
        );
    }

    public removeProgram(prgId: number): Observable<unknown> {
        return this.#b2BService.apiB2BRemoveB2BProgramDelete({ prgId })
            .pipe(
                startWithLoading(this, StartWithUnit.RequestRemoveB2BProgram),
                tap(() => this.requestPrograms()),
            );
    }

    public requestTickets(srvId: number): void {
        this.hold(this.#b2BService.apiB2BListB2BTariffsGet({ srvId })
            .pipe(
                startWithLoading(this, StartWithUnit.RequestB2BPrograms),
            ), this.actions.requestTickets);
    }

    public requestExportParticipants(prgId: number): Observable<Blob> {
        return this.#b2BService.apiB2BListB2BClientGet({ prgId }).pipe(
            startWithLoading(this, StartWithUnit.RequestB2BProgramParticipants),
        );
    }

    private requestPartners(): Observable<B2BPartnerListResponse[]> {
        return this.#b2BPartnerService.apiB2BB2BPartnerListGet();
    }

    private requestOrganizations(): Observable<B2BVisOrgListResponse[]> {
        return this.#b2BPartnerService.apiB2BB2BVisOrgListGet();
    }
}
